/* eslint-disable no-magic-numbers */
import React, { useState, useEffect, useContext } from 'react'

import userStatuses from 'constants/userStatuses'
import roles, { SECRETARIAT_ROLE_ID, BIRDLIFE_SECRETARIAT_ROLE_ID, RFP_ROLE_ID } from 'constants/roles'
import Select from 'components/Select'
import Pagination from 'features/Pagination'
import { requestGetUsers, requestUsersSearch } from 'api/user'
import ItemsPerPageDropdown from 'components/ItemsPerPageDropdown'
import sortDirection from 'constants/sortDirection'
import Table from 'components/Table'
import { usePagination } from 'hooks/usePagination'
import { makeUserColumns, makeUserRow } from './userTable'
import SearchInput from 'components/SearchInput'
import { CountriesContext } from 'providers/countries'

import {
  UsersFilterComponentWrapper,
  UsersFilterSearchWrapper,
  UsersFilterWrapper,
  UsersManagementFooter,
  UsersSearchWrapper,
  UsersManagementPageContainer,
  UsersSearchInputWrapper,
  ResponsiveComponent
} from './style'
import Modal from '../../components/Modal'
import TableUserModal from './TableUserModal'

const roleMapping = {
  Individual: 'Individual',
  'National Coordination Group': 'NCG',
  'Regional Focal Point': 'RFP'
}

const statusMapping = {
  active: 'Active',
  pending: 'Pending'
}

const formattedRoles = roles
  .filter((role) => {
    return role.value !== SECRETARIAT_ROLE_ID && role.value !== BIRDLIFE_SECRETARIAT_ROLE_ID && role.value !== RFP_ROLE_ID
  })
  .map((role) => {
    return {
      label: roleMapping[role.label],
      value: role.value
    }
  })

const formattedUserStatuses = userStatuses.map((status) => {
  return {
    label: statusMapping[status.label],
    value: status.value
  }
})

const defaultPaginationState = {
  totalNumber: 0,
  resultsPerPage: 12,
  totalPages: 0,
  currentPage: 1,
  hasNext: true,
  hasPrevious: false
}

let usersFetchAbortController = null
const UserManagement = () => {
  const [countryCode, setCountryCode] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [userStatus, setUserStatus] = useState(null)
  const [searchUserQuery, setSearchUserQuery] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalUser, setModalUser] = useState({})
  const { countriesOptions } = useContext(CountriesContext)
  const { pagination, setPagination, selectPage, nextPage, prevPage, nextSection, prevSection, changeResultsPerPage } =
    usePagination(defaultPaginationState)

  const [sorting, setSorting] = useState({
    sortBy: 'lastName',
    sortDirection: sortDirection.ASC
  })

  const formattedCountries = countriesOptions.map((item) => {
    return {
      label: item.label,
      value: item.code
    }
  })

  const [users, setUsers] = useState([])

  function getUsers() {
    if (usersFetchAbortController) {
      console.log('Aborting previous fetch...')
      setIsLoading(false)
      usersFetchAbortController.abort()
    }

    usersFetchAbortController = new AbortController()
    const isSearch = searchUserQuery.length >= 3
    const request = isSearch ? requestUsersSearch : requestGetUsers

    const params = isSearch
      ? {
          resultsPerPage: pagination.resultsPerPage,
          currentPage: pagination.currentPage,
          query: searchUserQuery.toLowerCase().trim(),
          sortBy: sorting.sortBy,
          sortDirection: sorting.sortDirection,
          signal: usersFetchAbortController.signal
        }
      : {
          resultsPerPage: pagination.resultsPerPage,
          currentPage: pagination.currentPage,
          userStatus: userStatus ? userStatus.value : '',
          role: userRole ? userRole.value : '',
          country: countryCode ? countryCode.value : '',
          sortBy: sorting.sortBy,
          sortDirection: sorting.sortDirection,
          signal: usersFetchAbortController.signal
        }

    console.log('Fetching users with params:', params)
    setIsLoading(true)

    request(params)
      .then((data) => {
        console.log('Response received:', data)

        if (!data || !data.users || !data.pagination) {
          console.error('Malformed response:', data)
        }

        setUsers(data.users || [])
        setPagination(data.pagination || defaultPaginationState)
      })
      .catch((err) => {
        if (err.name === 'AbortError') {
          console.warn('Fetch aborted.')
        } else {
          console.error('Error fetching users:', err)
        }
      })
      .finally(() => {
        setIsLoading(false)
        console.log('Fetch complete. isLoading set to false.')
      })
  }

  useEffect(() => {
    if (searchUserQuery.length > 2) {
      setCountryCode(null)
      setUserRole(null)
      setUserStatus(null)
    }
  }, [searchUserQuery])

  useEffect(() => {
    console.log('useEffect triggered:', {
      countryCode,
      userRole,
      userStatus,
      resultsPerPage: pagination.resultsPerPage,
      currentPage: pagination.currentPage,
      sortBy: sorting.sortBy,
      sortDirection: sorting.sortDirection,
      searchUserQuery
    })
    getUsers()
  }, [
    countryCode,
    userRole,
    userStatus,
    pagination.resultsPerPage,
    pagination.currentPage,
    sorting.sortBy,
    sorting.sortDirection,
    searchUserQuery
  ])

  const handleFilterAction = (e, callback) => {
    setSearchUserQuery('')
    if (e.value === null) {
      callback(null)
      return
    }
    callback(e)
    setPagination((s) => {
      return { ...s, currentPage: 1 }
    })
  }

  function handleUserStatus(e) {
    handleFilterAction(e, setUserStatus)
  }

  function handleUserRole(e) {
    handleFilterAction(e, setUserRole)
  }

  function handleCountryChange(e) {
    handleFilterAction(e, setCountryCode)
  }

  const clearFilters = () => {
    setCountryCode(null)
    setUserRole(null)
    setUserStatus(null)
    setSearchUserQuery('')
  }

  function handleSearch(e) {
    const newQuery = e.target.value
    console.log('Search input:', newQuery)
    setSearchUserQuery(newQuery)

    if (newQuery.length > 2) {
      console.log('Resetting pagination due to search')
      setPagination((s) => {
        return { ...s, currentPage: 1 }
      })
    }
  }

  function handleGetUserForModal(user) {
    setIsModalOpen(true)
    setModalUser(user)
  }

  const columns = makeUserColumns(sorting, setSorting)
  const rows = makeUserRow(users, handleGetUserForModal, getUsers)

  return (
    <>
      <UsersManagementPageContainer>
        <UsersFilterSearchWrapper>
          <UsersFilterWrapper data-test="quick-filter">
            <UsersFilterComponentWrapper>
              <Select
                field="country"
                value={countryCode}
                onChange={handleCountryChange}
                options={[{ label: 'View All', value: null }, ...formattedCountries]}
                placeholder="Country"
                height="40px"
                marginTop="0px"
              />
            </UsersFilterComponentWrapper>
            <UsersFilterComponentWrapper>
              <Select
                field="role"
                value={userRole}
                onChange={handleUserRole}
                options={[{ value: null, label: 'View All' }, ...formattedRoles]}
                placeholder="User type"
                height="40px"
                marginTop="0px"
              />
            </UsersFilterComponentWrapper>
            <UsersFilterComponentWrapper>
              <Select
                field="status"
                value={userStatus}
                onChange={handleUserStatus}
                options={[{ value: null, label: 'View All' }, ...formattedUserStatuses]}
                placeholder="Status"
                height="40px"
                marginTop="0px"
              />
            </UsersFilterComponentWrapper>
          </UsersFilterWrapper>
          <ResponsiveComponent>
            <UsersSearchWrapper>
              <UsersSearchInputWrapper>
                <SearchInput
                  value={searchUserQuery}
                  onChange={handleSearch}
                  placeholder="searchUserPlaceholder"
                  type="text"
                  dataTest="search-input"
                />
              </UsersSearchInputWrapper>
            </UsersSearchWrapper>
          </ResponsiveComponent>
        </UsersFilterSearchWrapper>
        <Table
          rows={rows}
          columns={columns}
          noResultMessage={searchUserQuery ? 'nothingMatchesYourSearch' : 'noMatchingFilterCriteria'}
          dataTest="user-list"
          clearFilters={clearFilters}
          clearFiltersMessage={searchUserQuery ? 'clearResults' : 'clearFilters'}
          isLoading={isLoading}
        />
        <UsersManagementFooter data-test="pagination">
          <ItemsPerPageDropdown
            currentPage={pagination.currentPage}
            resultsPerPage={pagination.resultsPerPage}
            totalNumber={pagination.totalNumber}
            onChange={changeResultsPerPage}
            dataTest="items-per-page"
          />
          {pagination.totalPages > 1 && (
            <Pagination
              activePages={pagination.currentPage}
              numberOfPages={pagination.totalPages}
              nextPage={nextPage}
              prevPage={prevPage}
              nextSection={nextSection}
              prevSection={prevSection}
              selectPage={selectPage}
              dataTest="page-number"
            />
          )}
        </UsersManagementFooter>
      </UsersManagementPageContainer>
      <Modal
        width={'600px'}
        isOpen={isModalOpen}
        toggleModal={setIsModalOpen}
        modalTitleId="manageUser"
        bottomBorder={false}
      >
        <TableUserModal user={modalUser} toggleModal={setIsModalOpen} getUsers={getUsers} />
      </Modal>
    </>
  )
}

export default UserManagement
